"use client";

import React from "react";
import { Flame, Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
function InnerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-primary/20 flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground mb-2">Registration Confirmed</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary/50 hover:bg-primary/10 font-mono uppercase tracking-widest text-xs">
          Add Another Recipient
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Sterling" className="rounded-md bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 px-5" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70 ml-2">
                Please provide your full legal name for our records.
              </FormDescription>
              <FormMessage className="ml-2" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="a.sterling@firm.com" className="rounded-md bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 px-5" {...field} />
              </FormControl>
              <FormMessage className="ml-2" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Invalid phone number format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono uppercase tracking-wider text-xs">Phone Number (optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="rounded-md bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 px-5" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70 ml-2">
                For urgent legal updates and consultation scheduling.
              </FormDescription>
              <FormMessage className="ml-2" />
            </FormItem>} />

        <FormField control={control} name="practice_area" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono uppercase tracking-wider text-xs">Area of Interest</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-12 w-full appearance-none rounded-md border border-border/50 bg-background/50 px-5 py-2 text-base shadow-xs transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                    <option value="" disabled className="text-muted-foreground">Select practice area</option>
                    {["Family Law", "Real Estate", "Corporate Law", "Litigation"].map((opt, i) => <option key={i} data-index={i} value={opt} className="bg-background text-foreground">
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <Flame className="size-4 text-primary/70" aria-hidden="true" />
                  </div>
                </div>
              </FormControl>
              <FormMessage className="ml-2" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md mt-6">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[240px] rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-sm h-14 shadow-lg transition-all duration-300 mx-auto block">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : 'Request Consultation'}
        </Button>
      </div>
    </Form>;
}
export default function SubscribeToUpdates() {
  return <section data-section-id="223928" id="subscribe-to-updates" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] pointer-events-none" aria-hidden="true" />
      
      <div className="max-w-4xl mx-auto relative">
        <div className="relative overflow-hidden rounded-md bg-gradient-to-br from-card to-background border border-border/50 p-8 sm:p-12 md:p-16 shadow-2xl shadow-black/20">
          
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/10 rounded-lg blur-[80px] pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-secondary/10 rounded-lg blur-[80px] pointer-events-none" aria-hidden="true" />

          <div className="relative z-10 text-center mb-10">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-4">
              Stay Informed
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
              Receive professional legal insights, firm updates, and priority scheduling for consultations directly from Sterling Law Associates.
            </p>
          </div>

          <FormProvider formKey="subscribe_form_legal_insights" formId="subscribe_form_legal_insights" sectionName="subscribe-to-updates" defaultValues={{
          name: "",
          email: "",
          phone: "",
          practice_area: ""
        }} submitText="Request Consultation" successMessage="Your request has been received. A representative from Sterling Law Associates will contact you shortly.">
            <InnerCircleForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}